import { Page, PageHeader } from '../components/Page';
import { StatCard } from '../components/StatCard';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { InvoiceTable } from '../components/InvoiceTable';
import { Async } from '../components/Async';
import { IcCreditCard, IcBuilding, IcCalendar } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { getSummary, getClients, getInvoices, getActivePatients } from '../lib/api';
import { fmtCents0, fmtCents, fmtNum } from '../lib/format';

export const PlatformFeesPage = () => {
  const summary = useFetch(getSummary);
  const clients = useFetch(getClients);
  const invoices = useFetch(() => getInvoices({ flow: 'platform' }));
  const active = useFetch(getActivePatients);

  const platformTotal = summary.data?.byFlow.find((b) => b.flow === 'platform')?.totalCents ?? 0;
  const activeById = new Map((active.data?.clients ?? []).map((c) => [c.clientId, c.activePatients]));

  return (
    <Page>
      <PageHeader
        title="Platform Fees"
        sub="Active-patient floor + per-patient overage · Stripe · settled monthly on the 1st"
        actions={<button className="btn btn--primary">Run platform billing</button>}
      />

      <div className="grid grid-3">
        <StatCard tone="in-progress" icon={IcCreditCard} label="Platform invoiced" value={fmtCents0(platformTotal)} />
        <StatCard tone="staged" icon={IcBuilding} label="Clients" value={fmtNum(summary.data?.clientCount ?? 0)} />
        <StatCard tone="success" icon={IcCalendar} label="Next run" value="1st" sub="via Stripe" />
      </div>

      <div>
        <div className="section-title">Plan by client</div>
        <Async state={clients} isEmpty={(d) => d.clients.length === 0} emptyLabel="No clients yet.">
          {({ clients: rows }) => (
            <PCard>
              <table className="p-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Patient floor</th>
                    <th>Active patients{active.data ? ` (${active.data.period})` : ''}</th>
                    <th>Base fee</th>
                    <th>Per patient</th>
                    <th>Pharmacies</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const ap = activeById.get(c.id);
                    return (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td className="tabular muted">{c.type === 'telemed' ? '—' : fmtNum(c.minPatients)}</td>
                      <td className="tabular" style={{ fontWeight: 500 }}>
                        {c.type === 'telemed'
                          ? '—'
                          : active.loading
                            ? '…'
                            : ap == null
                              ? '—'
                              : fmtNum(ap)}
                      </td>
                      <td className="tabular">{fmtCents0(c.baseFeeCents)}</td>
                      <td className="tabular muted">{fmtCents(c.perPatientCents)}</td>
                      <td className="tabular muted">{c.pharmacyCount}</td>
                      <td>
                        <StatusPill tone={c.status === 'active' ? 'success' : 'neutral'}>
                          {c.status === 'active' ? 'Active' : c.status === 'paused' ? 'Paused' : 'Churned'}
                        </StatusPill>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </PCard>
          )}
        </Async>
      </div>

      <div>
        <div className="section-title">Platform invoices</div>
        <Async state={invoices} isEmpty={(d) => d.invoices.length === 0} emptyLabel="No platform invoices yet.">
          {({ invoices: rows }) => <InvoiceTable invoices={rows} showType={false} />}
        </Async>
      </div>
    </Page>
  );
};
